import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const ContactForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    inquiryType: '',
    message: ''
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const inquiryTypes = [
    { value: 'general', label: 'General Inquiry', icon: 'MessageCircle' },
    { value: 'custom-cake', label: 'Custom Cake Order', icon: 'Cake' },
    { value: 'catering', label: 'Catering Request', icon: 'ChefHat' },
    { value: 'feedback', label: 'Feedback', icon: 'Star' }
  ];

  const handleChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors?.[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleInquiryTypeSelect = (type) => {
    setFormData(prev => ({ ...prev, inquiryType: type }));
    if (errors?.inquiryType) {
      setErrors(prev => ({ ...prev, inquiryType: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.name?.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData?.email?.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(formData?.email)) {
      newErrors.email = 'Please enter a valid email';
    }

    if (!formData?.phone?.trim()) {
      newErrors.phone = 'Phone number is required';
    }

    if (!formData?.inquiryType) {
      newErrors.inquiryType = 'Please select an inquiry type';
    }

    if (!formData?.message?.trim()) {
      newErrors.message = 'Message is required';
    } else if (formData?.message?.trim()?.length < 10) {
      newErrors.message = 'Message must be at least 10 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = (e) => {
    e?.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    setTimeout(() => {
      setIsSubmitting(false);
      setSubmitSuccess(true);
      setFormData({
        name: '',
        email: '',
        phone: '',
        inquiryType: '',
        message: ''
      });

      setTimeout(() => {
        setSubmitSuccess(false);
      }, 5000);
    }, 1500);
  };

  return (
    <div className="bg-card rounded-2xl overflow-hidden shadow-warm">
      <div className="p-6 border-b border-border">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
            <Icon name="Send" size={20} color="var(--color-primary)" />
          </div>
          <h2 className="text-2xl font-serif font-semibold text-foreground">
            Send Us a Message
          </h2>
        </div>
        <p className="text-muted-foreground">
          Have a question or special request? We'd love to hear from you
        </p>
      </div>
      <div className="p-6">
        {submitSuccess && (
          <div className="mb-6 p-4 bg-success/10 border border-success/20 rounded-lg">
            <div className="flex items-start gap-3">
              <Icon name="CheckCircle" size={20} color="var(--color-success)" className="mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium text-success mb-1">
                  Message Sent Successfully!
                </p>
                <p className="text-sm text-muted-foreground">
                  Thank you for contacting us. We'll respond within 24 hours.
                </p>
              </div>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Full Name"
              type="text"
              name="name"
              placeholder="John Doe"
              value={formData?.name}
              onChange={handleChange}
              error={errors?.name}
              required
            />

            <Input
              label="Email Address"
              type="email"
              name="email"
              placeholder="john@example.com"
              value={formData?.email}
              onChange={handleChange}
              error={errors?.email}
              required
            />
          </div>

          <Input
            label="Phone Number"
            type="tel"
            name="phone"
            placeholder="(416) 555-0123"
            value={formData?.phone}
            onChange={handleChange}
            error={errors?.phone}
            required
          />

          <div>
            <label className="block text-sm font-medium text-foreground mb-3">
              Inquiry Type <span className="text-error">*</span>
            </label>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {inquiryTypes?.map((type) => (
                <button
                  key={type?.value}
                  type="button"
                  onClick={() => handleInquiryTypeSelect(type?.value)}
                  className={`p-4 rounded-xl border-2 transition-all text-center ${
                    formData?.inquiryType === type?.value
                      ? 'border-primary bg-primary/5' :'border-border bg-muted/30 hover:border-primary/30'
                  }`}
                >
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center mx-auto mb-2 ${
                    formData?.inquiryType === type?.value
                      ? 'bg-primary/20' :'bg-primary/10'
                  }`}>
                    <Icon 
                      name={type?.icon} 
                      size={20} 
                      color="var(--color-primary)"
                    />
                  </div>
                  <p className={`text-sm font-medium ${
                    formData?.inquiryType === type?.value
                      ? 'text-primary' :'text-foreground'
                  }`}>
                    {type?.label}
                  </p>
                </button>
              ))}
            </div>
            {errors?.inquiryType && (
              <p className="mt-2 text-sm text-error">{errors?.inquiryType}</p>
            )}
          </div>

          <div>
            <label htmlFor="message" className="block text-sm font-medium text-foreground mb-2">
              Message <span className="text-error">*</span>
            </label>
            <textarea
              id="message"
              name="message"
              rows="5"
              placeholder="Tell us about your inquiry..."
              value={formData?.message}
              onChange={handleChange}
              className={`w-full px-4 py-3 rounded-lg border bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-all resize-none ${
                errors?.message ? 'border-error' : 'border-input'
              }`}
            />
            {errors?.message && (
              <p className="mt-2 text-sm text-error">{errors?.message}</p>
            )}
          </div>

          <Button
            type="submit"
            variant="default"
            fullWidth
            loading={isSubmitting}
            iconName="Send"
            iconPosition="right"
          >
            {isSubmitting ? 'Sending...' : 'Send Message'}
          </Button>
        </form>

        <div className="mt-6 p-4 bg-muted/30 rounded-lg">
          <div className="flex items-start gap-3">
            <Icon name="Clock" size={20} color="var(--color-primary)" className="mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm font-medium text-foreground mb-1">
                Response Time
              </p>
              <p className="text-sm text-muted-foreground">
                We typically respond to all inquiries within 24 hours during business days. For urgent matters, please call us directly at (416) 555-0123.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactForm;